import { useTranslations } from 'next-intl'
import { ChangeEventHandler, FC, useEffect, useState } from 'react'
import useActionPointCreateV1 from '@/api/admin/actionPoints/useActionPointCreateV1'
import useActionPointUpdateV1 from '@/api/admin/actionPoints/useActionPointUpdateV1'
import { dimensions, zonesOrder } from '@/app/dashboard/data'
import Button from '@/components/ui/button'
import { ActionPointTopic, SurveyGroup, type ActionPoint, type RiskZoneId, type Topic } from '@/types'
import { cn } from '@/utils/tailwind/utils'

type Props = {
  className?: string
  actionPoint?: ActionPoint
  openTechnicalDetails?: boolean
  onClose?: () => void
  onUpdate?: () => void
}

const ActionPointForm: FC<Props> = ({
  actionPoint,
  className = '',
  openTechnicalDetails = false,
  onClose,
  onUpdate = () => {},
}) => {
  const isAlreadySavedAP = actionPoint?.id !== undefined && actionPoint?.id !== ''

  const create = useActionPointCreateV1()
  const update = useActionPointUpdateV1()
  const { mutate, isPending: isLoading } = isAlreadySavedAP ? update : create

  const surveyGroups = [SurveyGroup.EMPLOYEE, SurveyGroup.PARTNER, SurveyGroup.CUSTOMER]

  const defaultId = actionPoint?.id || ''
  const defaultDimension = actionPoint?.dimension || 'sincerity'
  const defaultZone = actionPoint?.zone || zonesOrder[0]
  const defaultTopic = actionPoint?.topic || ActionPointTopic.acknowledgement
  const defaultPriority = actionPoint?.priority || 1
  const defaultTextDe = actionPoint?.texts?.de.text || ''
  const defaultTextEn = actionPoint?.texts?.en.text || ''
  const defaultProblemIndicatorsDe = (actionPoint?.texts?.de.problemIndicators || []).join('\n')
  const defaultProblemIndicatorsEn = (actionPoint?.texts?.en.problemIndicators || []).join('\n')
  const defaultSolutionIndicatorsDe = (actionPoint?.texts?.de.solutionIndicators || []).join('\n')
  const defaultSolutionIndicatorsEn = (actionPoint?.texts?.en.solutionIndicators || []).join('\n')
  const defaultDescription1De = actionPoint?.texts?.de.details?.description1 || ''
  const defaultDescription1En = actionPoint?.texts?.en.details?.description1 || ''
  const defaultDescription2De = actionPoint?.texts?.de.details?.description2 || ''
  const defaultDescription2En = actionPoint?.texts?.en.details?.description2 || ''
  const defaultPointsDe = (actionPoint?.texts?.de.details?.points || []).join('\n')
  const defaultPointsEn = (actionPoint?.texts?.en.details?.points || []).join('\n')
  const defaultSurveyGroupType = actionPoint?.surveyGroupType || SurveyGroup.EMPLOYEE

  const [actionPointId, setActionPointId] = useState(defaultId)
  const [actionPointDimension, setActionPointDimension] = useState(defaultDimension)
  const [actionPointZone, setActionPointZone] = useState(defaultZone)
  const [actionPointTopic, setActionPointTopic] = useState<Topic['id']>(defaultTopic)
  const [actionPointPriority, setActionPointPriority] = useState(defaultPriority)

  const [actionPointTextDe, setActionPointTextDe] = useState(defaultTextDe)
  const [actionPointTextEn, setActionPointTextEn] = useState(defaultTextEn)

  const [problemIndicatorsDe, setProblemIndicatorsDe] = useState(defaultProblemIndicatorsDe)
  const [problemIndicatorsEn, setProblemIndicatorsEn] = useState(defaultProblemIndicatorsEn)
  const [solutionIndicatorsDe, setSolutionIndicatorsDe] = useState(defaultSolutionIndicatorsDe)
  const [solutionIndicatorsEn, setSolutionIndicatorsEn] = useState(defaultSolutionIndicatorsEn)

  const [description1De, setDescription1De] = useState(defaultDescription1De)
  const [description1En, setDescription1En] = useState(defaultDescription1En)
  const [description2De, setDescription2De] = useState(defaultDescription2De)
  const [description2En, setDescription2En] = useState(defaultDescription2En)
  const [pointsDe, setPointsDe] = useState(defaultPointsDe)
  const [pointsEn, setPointsEn] = useState(defaultPointsEn)

  const [surveyGroupType, setSurveyGroupType] = useState(defaultSurveyGroupType)

  const [editTechnicalProperties, setEditTechnicalProperties] = useState(
    actionPoint === undefined || openTechnicalDetails,
  )

  const [errorMessages, setErrorMessages] = useState<Record<string, any>>({})
  const [successMessage, setSuccessMessage] = useState('')

  useEffect(() => {
    setActionPointId(actionPoint?.id || '')
    setActionPointDimension(actionPoint?.dimension || 'sincerity')
    setActionPointZone(actionPoint?.zone || zonesOrder[0])
    setActionPointTopic(actionPoint?.topic || ActionPointTopic.acknowledgement)
    setActionPointPriority(actionPoint?.priority || 1)
    setActionPointTextDe(actionPoint?.texts?.de?.text || '')
    setActionPointTextEn(actionPoint?.texts?.en?.text || '')
    setProblemIndicatorsDe(actionPoint?.texts?.de?.problemIndicators?.join('\n') || '')
    setProblemIndicatorsEn(actionPoint?.texts?.en?.problemIndicators?.join('\n') || '')
    setSolutionIndicatorsDe(actionPoint?.texts?.de?.solutionIndicators?.join('\n') || '')
    setSolutionIndicatorsEn(actionPoint?.texts?.en?.solutionIndicators?.join('\n') || '')
    setDescription1De(actionPoint?.texts?.de.details?.description1 || '')
    setDescription1En(actionPoint?.texts?.en.details?.description1 || '')
    setDescription2De(actionPoint?.texts?.de.details?.description2 || '')
    setDescription2En(actionPoint?.texts?.en.details?.description2 || '')
    setPointsDe(actionPoint?.texts?.de.details?.points?.join('\n') || '')
    setPointsEn(actionPoint?.texts?.en.details?.points?.join('\n') || '')
    setSurveyGroupType(actionPoint?.surveyGroupType || SurveyGroup.EMPLOYEE)
  }, [actionPoint])

  const reset = () => {
    console.log('*** reset AP:', actionPoint)
    setActionPointId(defaultId)
    setActionPointDimension(defaultDimension)
    setActionPointZone(defaultZone)
    setActionPointTopic(defaultTopic)
    setActionPointPriority(defaultPriority)
    setActionPointTextDe(defaultTextDe)
    setActionPointTextEn(defaultTextEn)
    setProblemIndicatorsDe(defaultProblemIndicatorsDe)
    setProblemIndicatorsEn(defaultProblemIndicatorsEn)
    setSolutionIndicatorsDe(defaultSolutionIndicatorsDe)
    setSolutionIndicatorsEn(defaultSolutionIndicatorsEn)
    setDescription1De(defaultDescription1De)
    setDescription1En(defaultDescription1En)
    setDescription2De(defaultDescription2De)
    setDescription2En(defaultDescription2En)
    setPointsDe(defaultPointsDe)
    setPointsEn(defaultPointsEn)
    setSurveyGroupType(defaultSurveyGroupType)
    setErrorMessages({})
    setSuccessMessage('')
  }

  const isDirty = () => {
    return (
      actionPointId !== defaultId ||
      actionPointDimension !== defaultDimension ||
      actionPointZone !== defaultZone ||
      actionPointTopic !== defaultTopic ||
      actionPointPriority !== defaultPriority ||
      actionPointTextDe !== defaultTextDe ||
      actionPointTextEn !== defaultTextEn ||
      problemIndicatorsDe !== defaultProblemIndicatorsDe ||
      problemIndicatorsEn !== defaultProblemIndicatorsEn ||
      solutionIndicatorsDe !== defaultSolutionIndicatorsDe ||
      solutionIndicatorsEn !== defaultSolutionIndicatorsEn ||
      description1De !== defaultDescription1De ||
      description1En !== defaultDescription1En ||
      description2De !== defaultDescription2De ||
      description2En !== defaultDescription2En ||
      pointsDe !== defaultPointsDe ||
      pointsEn !== defaultPointsEn ||
      surveyGroupType !== defaultSurveyGroupType
    )
  }

  const handleIdInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setActionPointId(e.target.value)
  }

  const handleDimensionChange: ChangeEventHandler<HTMLSelectElement> = (e) => {
    setActionPointDimension(e.target.value as ActionPoint['dimension'])
  }

  const handleZoneChange: ChangeEventHandler<HTMLSelectElement> = (e) => {
    setActionPointZone(e.target.value as RiskZoneId)
  }

  const handleTopicChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setActionPointTopic(e.target.value as Topic['id'])
  }

  const handlePriorityChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setActionPointPriority(parseFloat(e.target.value))
  }

  const handleTextDeInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setActionPointTextDe(e.target.value)
  }

  const handleTextEnInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setActionPointTextEn(e.target.value)
  }

  const handleProblemIndicatorsDeChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setProblemIndicatorsDe(e.target.value)
  }

  const handleProblemIndicatorsEnChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setProblemIndicatorsEn(e.target.value)
  }

  const handleSolutionIndicatorsDeChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setSolutionIndicatorsDe(e.target.value)
  }

  const handleSolutionIndicatorsEnChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setSolutionIndicatorsEn(e.target.value)
  }

  const handleDescription1DeChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setDescription1De(e.target.value)
  }

  const handleDescription1EnChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setDescription1En(e.target.value)
  }

  const handleDescription2DeChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setDescription2De(e.target.value)
  }

  const handleDescription2EnChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setDescription2En(e.target.value)
  }

  const handlePointsDeChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setPointsDe(e.target.value)
  }

  const handlePointsEnChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setPointsEn(e.target.value)
  }

  const handleSurveyGroupTypeChange: ChangeEventHandler<HTMLSelectElement> = (e) => {
    setSurveyGroupType(e.target.value as SurveyGroup.EMPLOYEE | SurveyGroup.PARTNER)
  }

  const splitTextAreaValue = (text: string) =>
    text
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)

  const handleSubmit = async () => {
    const messages: Record<string, any> = {}
    if (!actionPointId || actionPointId.trim() === '') {
      messages['actionPointId'] = 'ID must not be empty'
    }
    if (actionPointPriority < 1) {
      messages['actionPointPriority'] = 'Priority must be greater than 0'
    }

    setErrorMessages(messages)

    if (Object.keys(messages).length > 0) {
      return
    }

    try {
      mutate(
        {
          id: actionPointId.trim(),
          dimension: actionPointDimension,
          zone: actionPointZone,
          topic: actionPointTopic,
          priority: actionPointPriority,
          surveyGroupType,
          texts: {
            de: {
              text: actionPointTextDe,
              problemIndicators: splitTextAreaValue(problemIndicatorsDe),
              solutionIndicators: splitTextAreaValue(solutionIndicatorsDe),
              details: {
                description1: description1De,
                description2: description2De,
                points: splitTextAreaValue(pointsDe),
              },
            },
            en: {
              text: actionPointTextEn,
              problemIndicators: splitTextAreaValue(problemIndicatorsEn),
              solutionIndicators: splitTextAreaValue(solutionIndicatorsEn),
              details: {
                description1: description1En,
                description2: description2En,
                points: splitTextAreaValue(pointsEn),
              },
            },
          },
        },
        {
          onSuccess: (_data) => {
            reset()
            setSuccessMessage(actionPoint ? 'Updated' : 'Created')
            setTimeout(() => setSuccessMessage(''), 5000)
            onUpdate()
          },
          onError: (error) => {
            console.warn('## error:', error)
            setErrorMessages({ global: error.message })
            setSuccessMessage('')
          },
        },
      )
    } catch (error) {
      console.error('API request failed', error)
      setErrorMessages({ global: 'Error creating a new action point. Please try again.' })
      setSuccessMessage('')
    }
  }

  const t = useTranslations()
  const translateDimension = (dimension: ActionPoint['dimension']) => t(`Dimension-labels-${dimension}`)
  const translateRiskZone = (riskZone: RiskZoneId) => t(`RiskZone-labels-${riskZone}`)

  const textArea = (props: {
    id: string
    label: string
    value: string
    placeholder: string
    className?: string
    onChange: ChangeEventHandler<HTMLTextAreaElement>
  }) => {
    return (
      <div className={`mt-4 flex-1 ${props.className || ''}`}>
        <label htmlFor={props.id} className="mb-2 mt-2 block w-full text-sm font-bold text-gray-700">
          {props.label}
        </label>
        <div className="flex-1">
          <textarea
            id={props.id}
            className="focus:shadow-outline w-full appearance-none rounded px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none"
            rows={4}
            placeholder={props.placeholder}
            value={props.value}
            onChange={props.onChange}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={cn('bg-slate-50', className)}>
      <form className="py-4">
        <div className="flex flex-wrap">
          <div className="flex w-full flex-wrap px-2 pb-4">
            <div className="flex w-1/2 odd:pr-4 even:pl-4">
              <label htmlFor="actionPointId" className="mb-2 mt-2 block w-32 text-sm font-bold text-gray-700">
                Action point ID:
              </label>
              <div className="flex-1">
                <input
                  type="text"
                  id="actionPointId"
                  value={actionPointId}
                  required
                  disabled={isAlreadySavedAP}
                  onChange={handleIdInputChange}
                  className={`focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none ${
                    errorMessages?.['actionPointId'] ? 'border-red-400' : ''
                  } ${isAlreadySavedAP ? 'bg-gray-200' : ''}`}
                />
                <p className="text-sm text-red-400">{errorMessages?.['actionPointId']}</p>
              </div>
            </div>

            <div className="flex w-1/2 odd:pr-4 even:pl-4">
              <label htmlFor="actionPointDimension" className="mb-2 mt-2 block w-32 text-sm font-bold text-gray-700">
                Dimension:
              </label>
              <div className="flex-1">
                <select
                  className="h-10 w-full rounded bg-gray-200 px-2 text-gray-700"
                  name="actionPointDimension"
                  id="actionPointDimension"
                  value={actionPointDimension}
                  onChange={handleDimensionChange}>
                  {dimensions.map((dimension) => {
                    if (dimension !== 'trusty') {
                      return (
                        <option key={dimension} value={dimension}>
                          {translateDimension(dimension)}
                        </option>
                      )
                    }
                  })}
                </select>
                <p className="text-sm text-red-400">{errorMessages?.['actionPointDimension']}</p>
              </div>
            </div>

            <div className="mt-4 flex w-1/2 odd:pr-4 even:pl-4">
              <label htmlFor="actionPointTextDe" className="mb-2 mt-2 block w-32 text-sm font-bold text-gray-700">
                Title (de):
              </label>
              <div className="flex-1">
                <input
                  type="text"
                  id="actionPointTextDe"
                  value={actionPointTextDe}
                  required
                  onChange={handleTextDeInputChange}
                  className={`focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none ${
                    errorMessages?.['actionPointTextDe'] ? 'border-red-400' : ''
                  }`}
                />
                <p className="text-sm text-red-400">{errorMessages?.['actionPointTextDe']}</p>
              </div>
            </div>

            <div className="mt-4 flex w-1/2 odd:pr-4 even:pl-4">
              <label htmlFor="actionPointTextEn" className="mb-2 mt-2 block w-32 text-sm font-bold text-gray-700">
                Title (en):
              </label>
              <div className="flex-1">
                <input
                  type="text"
                  id="actionPointTextEn"
                  value={actionPointTextEn}
                  required
                  onChange={handleTextEnInputChange}
                  className={`focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none ${
                    errorMessages?.['actionPointTextEn'] ? 'border-red-400' : ''
                  }`}
                />
                <p className="text-sm text-red-400">{errorMessages?.['actionPointTextEn']}</p>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-wrap border-b-2 px-2 pb-4">
            <div className="flex w-full">
              {textArea({
                id: 'description1De',
                label: 'Description 1 (de):',
                value: description1De,
                placeholder: 'Enter first description text',
                onChange: handleDescription1DeChange,
                className: 'mr-4',
              })}

              {textArea({
                id: 'description1En',
                label: 'Description 1 (en):',
                value: description1En,
                placeholder: 'Enter first description text',
                onChange: handleDescription1EnChange,
                className: 'ml-4',
              })}
            </div>
            <div className="flex w-full">
              {textArea({
                id: 'description2De',
                label: 'Description 2 (de):',
                value: description2De,
                placeholder: 'Enter second description text',
                onChange: handleDescription2DeChange,
                className: 'mr-4',
              })}

              {textArea({
                id: 'description2En',
                label: 'Description 2 (en):',
                value: description2En,
                placeholder: 'Enter second description text',
                onChange: handleDescription2EnChange,
                className: 'ml-4',
              })}
            </div>
            <div className="flex w-full">
              {textArea({
                id: 'pointsDe',
                label: 'Points (de):',
                value: pointsDe,
                placeholder: 'Enter one point per line',
                onChange: handlePointsDeChange,
                className: 'mr-4',
              })}

              {textArea({
                id: 'pointsEn',
                label: 'Points (en):',
                value: pointsEn,
                placeholder: 'Enter one point per line',
                onChange: handlePointsEnChange,
                className: 'ml-4',
              })}
            </div>
          </div>
        </div>
        <div className="mt-4 flex w-full flex-wrap px-2">
          <div className="mb-2 w-full">
            <div className="text-gray-500">
              <p className="mb-1 font-bold">Technical details</p>
              <p className="text-sm">
                Technical properties are fields which are not displayed to customers, but are used for the
                recommendation engine.
                {actionPoint && ' By editing them, you are going to affect the outcome of the recommendation engine.'}
              </p>
            </div>
            {actionPoint && (
              <div className="mt-4 flex text-gray-500">
                <label role="button" className="flex items-center gap-2 text-sm">
                  Edit the technical details?
                  <input
                    role="button"
                    type="checkbox"
                    checked={editTechnicalProperties}
                    onChange={() => setEditTechnicalProperties(!editTechnicalProperties)}
                  />
                </label>
              </div>
            )}
          </div>
          <div
            className={`w-full transition-all ${
              !editTechnicalProperties ? 'max-h-0 overflow-hidden' : 'z-0 max-h-[800px]'
            }`}>
            <div className="flex w-full flex-wrap">
              <div className="mt-4 flex w-full pr-4 last:pr-0 lg:w-3/12">
                <label htmlFor="actionPointZone" className="mb-2 mt-2 block w-16 text-sm font-bold text-gray-700">
                  Zone:
                </label>
                <div className="flex-1">
                  <select
                    className="h-10 w-full rounded bg-gray-200 px-2 text-gray-700"
                    name="actionPointZone"
                    id="actionPointZone"
                    value={actionPointZone}
                    onChange={handleZoneChange}
                    disabled={!editTechnicalProperties}>
                    {zonesOrder.map((zone) => (
                      <option key={zone} value={zone}>
                        {translateRiskZone(zone)}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-red-400">{errorMessages?.['actionPointZone']}</p>
                </div>
              </div>

              <div className="mt-4 flex w-full pr-4 last:pr-0 lg:w-3/12">
                <label htmlFor="surveyGroupType" className="mt-2 block w-16 text-sm font-bold text-gray-700">
                  Type:
                </label>
                <div className="flex-1">
                  <select
                    className="h-10 w-full rounded bg-gray-200 px-2 text-gray-700"
                    name="surveyGroupType"
                    id="surveyGroupType"
                    value={surveyGroupType}
                    onChange={handleSurveyGroupTypeChange}
                    disabled={!editTechnicalProperties}>
                    {surveyGroups.map((group) => (
                      <option key={group} value={group}>
                        {group}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4 flex w-full pr-4 last:pr-0 lg:w-3/12">
                <label htmlFor="actionPointTopic" className="mb-2 mt-2 block w-16 text-sm font-bold text-gray-700">
                  Topic:
                </label>
                <div className="flex-1">
                  <input
                    type="text"
                    id="actionPointTopic"
                    list="topicList"
                    required
                    value={actionPointTopic}
                    onChange={handleTopicChange}
                    className={`focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none ${
                      errorMessages?.['actionPointTopic'] ? 'border-red-400' : ''
                    }`}
                    disabled={!editTechnicalProperties}
                  />
                  <datalist id="topicList">
                    {Object.keys(ActionPointTopic).map((topic) => (
                      <option key={topic} value={topic}>
                        {topic}
                      </option>
                    ))}
                  </datalist>
                  <p className="text-sm text-red-400">{errorMessages?.['actionPointTopic']}</p>
                </div>
              </div>

              <div className="mt-4 flex w-4/12 pr-4 last:pr-0 lg:w-3/12">
                <label htmlFor="actionPointPriority" className="mt-2 block w-16 text-sm font-bold text-gray-700">
                  Priority:
                </label>
                <div className="flex-1">
                  <input
                    type="number"
                    min="0"
                    id="actionPointPriority"
                    value={actionPointPriority}
                    required
                    onChange={handlePriorityChange}
                    className={`focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none ${
                      errorMessages?.['actionPointPriority'] ? 'border-red-400' : ''
                    }`}
                    disabled={!editTechnicalProperties}
                  />
                  <p className="text-sm text-red-400">{errorMessages?.['actionPointPriority']}</p>
                </div>
              </div>
            </div>
            <div className="flex w-full flex-wrap pb-4">
              <div className="flex w-full">
                {textArea({
                  id: 'problemIndicatorsDe',
                  label: 'Problem Indicators (de):',
                  value: problemIndicatorsDe,
                  placeholder: 'Enter one indicator per line',
                  onChange: handleProblemIndicatorsDeChange,
                  className: 'mr-4',
                })}

                {textArea({
                  id: 'problemIndicatorsEn',
                  label: 'Problem Indicators (en):',
                  value: problemIndicatorsEn,
                  placeholder: 'Enter one indicator per line',
                  onChange: handleProblemIndicatorsEnChange,
                  className: 'ml-4',
                })}
              </div>

              <div className="flex w-full">
                {textArea({
                  id: 'solutionIndicatorsDe',
                  label: 'Solution Indicators (de):',
                  value: solutionIndicatorsDe,
                  placeholder: 'Enter one indicator per line',
                  onChange: handleSolutionIndicatorsDeChange,
                  className: 'mr-4',
                })}

                {textArea({
                  id: 'solutionIndicatorsEn',
                  label: 'Solution Indicators (en):',
                  value: solutionIndicatorsEn,
                  placeholder: 'Enter one indicator per line',
                  onChange: handleSolutionIndicatorsEnChange,
                  className: 'ml-4',
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-row justify-end">
          {onClose && (
            <Button onClick={onClose} color="violet" className="mr-2" disabled={isLoading}>
              Cancel
            </Button>
          )}
          <Button onClick={handleSubmit} color={isDirty() ? 'green' : 'violet'} className="mr-2" disabled={isLoading}>
            Save
          </Button>

          {errorMessages?.['global'] && (
            <div className="align-center ml-4 text-sm text-red-400">{errorMessages['global']}</div>
          )}
          {successMessage && <span className="ml-4 inline-block align-middle text-green-600">{successMessage}</span>}
        </div>
      </form>
    </div>
  )
}

export default ActionPointForm
